import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DisciplineWithLessonsInput } from '@/hooks/useConfigExam';

interface DisciplineWithLessonStore {
  disciplinesWithLessons: DisciplineWithLessonsInput[];
  addDisciplineWithLesson: (
    disciplineWithLesson: DisciplineWithLessonsInput,
  ) => void;
  removeDisciplineWithLesson: (index: number) => void;
  getDisciplinesWithLessons: () => DisciplineWithLessonsInput[];
  syncWithServerData: (serverData: DisciplineWithLessonsInput[]) => void;
  updateDisciplineWithLesson: (
    index: number,
    disciplineWithLesson: DisciplineWithLessonsInput,
  ) => void;
}

export const useDisciplineWithLessonStore = create<DisciplineWithLessonStore>()(
  persist(
    (set, get) => ({
      disciplinesWithLessons: [],

      addDisciplineWithLesson: (disciplineWithLesson) =>
        set((state) => {
          const disciplineId =
            typeof disciplineWithLesson.discipline === 'string'
              ? disciplineWithLesson.discipline
              : disciplineWithLesson.discipline._id;

          const existingIndex = state.disciplinesWithLessons.findIndex(
            (item) => {
              const itemDisciplineId =
                typeof item.discipline === 'string'
                  ? item.discipline
                  : item.discipline._id;
              return itemDisciplineId === disciplineId;
            },
          );

          if (existingIndex !== -1) {
            // Si la discipline existe déjà, on combine les leçons
            const updatedDisciplines = [...state.disciplinesWithLessons];
            const existingLessons = updatedDisciplines[existingIndex].lessons;
            const newLessons = disciplineWithLesson.lessons;

            // Combiner les leçons en évitant les doublons
            const combinedLessons = [...existingLessons];
            newLessons.forEach((newLesson) => {
              const lessonExists = combinedLessons.some(
                (existingLesson) => existingLesson._id === newLesson._id,
              );
              if (!lessonExists) {
                combinedLessons.push(newLesson);
              }
            });

            updatedDisciplines[existingIndex] = {
              ...updatedDisciplines[existingIndex],
              lessons: combinedLessons,
            };
            return { disciplinesWithLessons: updatedDisciplines };
          }

          // Si la discipline n'existe pas, on l'ajoute
          return {
            disciplinesWithLessons: [
              ...state.disciplinesWithLessons,
              disciplineWithLesson,
            ],
          };
        }),

      removeDisciplineWithLesson: (index) =>
        set((state) => ({
          disciplinesWithLessons: state.disciplinesWithLessons.filter(
            (_, i) => i !== index,
          ),
        })),

      getDisciplinesWithLessons: () => get().disciplinesWithLessons,

      syncWithServerData: (serverData) => {
        const localData = get().disciplinesWithLessons;

        // Combiner les données locales avec les données du serveur
        const combinedData = [...serverData];

        localData.forEach((localItem) => {
          const localDisciplineId =
            typeof localItem.discipline === 'string'
              ? localItem.discipline
              : localItem.discipline._id;

          const existingIndex = combinedData.findIndex((serverItem) => {
            const serverDisciplineId =
              typeof serverItem.discipline === 'string'
                ? serverItem.discipline
                : serverItem.discipline._id;
            return serverDisciplineId === localDisciplineId;
          });

          if (existingIndex === -1) {
            // Si la discipline n'existe pas dans les données du serveur, l'ajouter
            combinedData.push(localItem);
          } else {
            // Si la discipline existe, combiner les leçons
            const existingLessons = combinedData[existingIndex].lessons;
            const newLessons = localItem.lessons;

            const combinedLessons = [...existingLessons];
            newLessons.forEach((newLesson) => {
              const lessonExists = combinedLessons.some(
                (existingLesson) => existingLesson._id === newLesson._id,
              );
              if (!lessonExists) {
                combinedLessons.push(newLesson);
              }
            });

            combinedData[existingIndex] = {
              ...combinedData[existingIndex],
              lessons: combinedLessons,
            };
          }
        });

        set({ disciplinesWithLessons: combinedData });
      },

      updateDisciplineWithLesson: (index, disciplineWithLesson) =>
        set((state) => {
          const updatedDisciplines = [...state.disciplinesWithLessons];
          updatedDisciplines[index] = disciplineWithLesson;
          return { disciplinesWithLessons: updatedDisciplines };
        }),
    }),
    {
      name: 'discipline-with-lesson-storage',
    },
  ),
);
